import { Product } from '@@types'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { useFetchProduct } from '@entities/Product'

import { CartPreview } from '@features/cart'
import { useAddressStore } from '@features/shipping-methods'

import NotFoundComponent from '@widgets/not-found/NotFoundComponent'
import { ProductDetail } from '@widgets/product-detail'
import { ProductsSimilar } from '@widgets/products-similar'


import { SEO } from '@shared/components/SEO'
import { BRAND_NAME } from '@shared/contants/app'
import { useAnalytics } from '@shared/lib/analytics'
import { createProductJsonLd } from '@shared/lib/seo/seo-utils'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import Wrapper from '@shared/ui/wrapper'


export default function ProductDetailPage () {
  const { t } = useTranslation('seo')
  const { t: tc } = useTranslation('common')
  const { slug } = useParams()
  const { shopId } = useAddressStore()
  const { product, loading } = useFetchProduct({
    shopId: shopId,
    slug,
    style: '600xauto',
    imageStyles: '1920xauto',
    galleryStyles: '1920xauto',
  })

  const { viewItem } = useAnalytics()

  useEffect(() => {
    if (product) {
      viewItem(product)
    }
  }, [ product?.id ])

  const jsonLd = useMemo(() => {
    if (!product) return []
    return [
      { id: 'product-jsonld', data: createProductJsonLd(product as Product) }
    ]
  }, [ product ])

  if (!loading && !product) {
    return (
      <Container>
        <PageGrid aside={<CartPreview />}>
          <Wrapper>
            <NotFoundComponent
              title={tc('product-not-found.title')}
              description={tc('product-not-found.description')}
            />
          </Wrapper>
        </PageGrid>
      </Container>
    )
  }

  return (
    <div className="product-page">
      {product && (
        <SEO
          title={t('product.title', { name: product.name })}
          description={product.short_description || t('product.description', { name: product.name, brand: BRAND_NAME })}
          keywords={`${product.name}, ${product.brand?.name || ''}, продукты, акции`}
          ogTitle={product.name}
          ogDescription={product.short_description || undefined}
          ogImage={product.preview?.url || product.fullPreview?.url || undefined}
          canonicalUrl={`/product/${slug}`}
          jsonLd={jsonLd}
        />
      )}
      <Container>
        <PageGrid aside={<CartPreview />}>
          <Wrapper
            style={{
              overflow: 'initial',
              position: 'relative',
            }}
          >
            {product && (
              <>
                <ProductDetail product={product} />
                <ProductsSimilar product={product} />
              </>
            )}
          </Wrapper>
        </PageGrid>
      </Container>
    </div>
  )
}